// import { useMemo, useState } from 'react'
// import { useStore, useFilteredStreams } from '@/store'
// import {  getRegionLabel, getSevColor, getSevLabel } from '@/constants'
// import { Tier } from '@/analysis/wsTypes'

// export default function StreamTable() {
//   const streams       = useFilteredStreams()
//   const dimension     = useStore(s => s.dimension)
//   const search        = useStore(s => s.searchResult)
//   const drillStream = useStore(s => s.drillStream)
//   const drillToStream = useStore(s => s.drillToStream)
//   const drillToRegion = useStore(s => s.drillToRegion)
//   const addComparisonStream = useStore(s => s.addComparisonStream)
//   const setUserId = useStore(s => s.setUserId)
//   const setView = useStore(s => s.setView)
//   const dataDebouncing = useStore(s => s.dataDebouncing)
//   const streamsLoading = useStore(s => s.streamsLoading)
//   const lastDataError  = useStore(s => s.lastDataError)
//   const [visible, setVisible] = useState(80)

//   type LayerKey = 'demuxInRate' | 'demuxToDecodeLagMs' | 'renderInOutGap' | 'downloadKbps'
//   type Layer = { key: LayerKey; label: string; tier: 0 | 1 | 2 | null; valueText: string | null }

//   const fmtMs = (v: number | null | undefined) =>
//     v === null || v === undefined || !Number.isFinite(v) ? null : `${Math.round(v)}ms`
//   const fmt = (v: number | null | undefined, suffix = '') =>
//     v === null || v === undefined || !Number.isFinite(v) ? '—' : `${Math.round(v)}${suffix}`

//   const layerCandidates = (s: (typeof streams)[number]): Layer[] => {
//     const p: any = s.pipeline ?? {}
//     const mk = (key: LayerKey, label: string, value: any, tier: any): Layer => ({
//       key,
//       label,
//       tier: (tier === 0 || tier === 1 || tier === 2) ? tier : null,
//       valueText: value === null || value === undefined || !Number.isFinite(Number(value))
//         ? null
//         : (key === 'demuxToDecodeLagMs') ? fmtMs(Number(value))
//           : key === 'demuxInRate' ? `${Number(value).toFixed(1)}/s`
//             : key === 'downloadKbps' ? `${Math.round(Number(value))}kbps`
//               : `${Math.round(Number(value))}`,
//     })
//     return [
//       mk('demuxInRate', 'Demux in', p.demuxInRate ?? null, p.demuxInRateTier ?? null),
//       mk('demuxToDecodeLagMs', 'Packet delay', p.demuxToDecodeLagMs ?? null, p.demuxToDecodeLagTier ?? null),
//       mk('renderInOutGap', 'Render gap', p.renderInOutGap ?? null, p.renderInOutGapTier ?? null),
//       mk('downloadKbps', 'Download', p.downloadKbps ?? null, p.downloadKbpsTier ?? null),
//     ]
//   }

//   const computeAvgAndWorst = (layers: Layer[]) => {
//     const tiers = layers.map(l => l.tier).filter((t): t is 0 | 1 | 2 => t === 0 || t === 1 || t === 2)
//     // Semantics: 0=healthy, 1=warn, 2=critical
//     const worst = tiers.length ? (Math.max(...tiers) as 0 | 1 | 2) : null
//     const avg = tiers.length ? (Math.max(0, Math.min(2, Math.round(tiers.reduce((a, b) => a + b, 0) / tiers.length))) as 0 | 1 | 2) : null
//     // Pick worst metric: highest tier, then prefer render > decode > demux > download.
//     const priority: Record<LayerKey, number> = { renderInOutGap: 4, demuxToDecodeLagMs: 3, demuxInRate: 2, downloadKbps: 1 }
//     const worstMetric = [...layers]
//       .filter(l => (l.tier === 0 || l.tier === 1 || l.tier === 2) && l.valueText != null)
//       .sort((a, b) => (b.tier! - a.tier!) || (priority[b.key] - priority[a.key]))[0] ?? null
//     return { avgSeverity: avg, worstMetricSeverity: worst, worstMetric }
//   }

//   const p95 = (vals: number[]) => {
//     const xs = vals.filter((v) => Number.isFinite(v)).sort((a, b) => a - b)
//     if (!xs.length) return null
//     const idx = Math.min(xs.length - 1, Math.max(0, Math.ceil(xs.length * 0.95) - 1))
//     return xs[idx]!
//   }

//   const valOfGroup = (list: typeof streams, key: LayerKey) => {
//     const vals = list.map(x => Number((x.pipeline as any)?.[key])).filter(Number.isFinite)
//     return p95(vals)
//   }

//   const sevColor = (tier: 0 | 1 | 2 | null | undefined) =>
//     getSevColor((tier === 0 || tier === 1 || tier === 2) ? tier : null)

//   const severityCell = (avg: 0 | 1 | 2 | null, worst: 0 | 1 | 2 | null, tooltip: string) => {
//     const cAvg = sevColor(avg ?? null)
//     const cWorst = sevColor(worst ?? null)
//     const avgNoData = !(avg === 0 || avg === 1 || avg === 2)
//     const worstNoData = !(worst === 0 || worst === 1 || worst === 2)
//     const ndFill = 'rgba(255,255,255,0.14)'
//     const ndStroke = 'rgba(255,255,255,0.26)'
//     return (
//       <div style={{ display: 'grid', placeItems: 'center', gap: 4 }} title={tooltip}>
//         <svg width="28" height="30" viewBox="0 0 28 30" style={{ display: 'block' }}>
//           <title>{tooltip}</title>
//           {/* average (back) */}
//           <polygon
//             points="14,2 26,8 26,22 14,28 2,22 2,8"
//             fill={avgNoData ? ndFill : cAvg}
//             opacity={avgNoData ? 0.38 : 0.22}
//             stroke={avgNoData ? ndStroke : cAvg}
//             strokeWidth="1"
//           />
//           {/* worst (front) */}
//           <polygon
//             points="14,6 22,10 22,20 14,24 6,20 6,10"
//             fill={worstNoData ? ndFill : cWorst}
//             opacity={worstNoData ? 0.50 : 0.35}
//             stroke={worstNoData ? ndStroke : cWorst}
//             strokeWidth="1.2"
//           />
//         </svg>
//       </div>
//     )
//   }

//   const rows = useMemo(() => {
//     // For non-stream dimensions, use the unified search payload directly so REST+WS stay consistent.
//     if (dimension !== 'stream' && search?.nodes && Array.isArray(search.nodes)) {
//       return (search.nodes as any[]).map((n) => ({
//         kind: 'group' as const,
//         id: String(n.id ?? n.label ?? ''),
//         label: String(n.label ?? n.id ?? ''),
//         totalStreams: Number(n.total ?? 0),
//         usersTotal: Number(n.usersTotal ?? 0),
//         hex: {
//           values: {
//             demuxInRate: Number.isFinite(Number(n.demuxInRate)) ? Number(n.demuxInRate) : null,
//             demuxToDecodeLagMs: Number.isFinite(Number(n.demuxToDecodeLagMs)) ? Number(n.demuxToDecodeLagMs) : null,
//             // API provides render ratio; table column is "Render in/out" legacy name.
//             renderInOutGap: Number.isFinite(Number(n.renderInOutGap)) ? Number(n.renderInOutGap) : null,
//             downloadKbps: Number.isFinite(Number(n.downloadKbps)) ? Number(n.downloadKbps) : null,
//           },
//           tiers: {
//             demuxInRate: (n.demuxInRateTier === 0 || n.demuxInRateTier === 1 || n.demuxInRateTier === 2) ? n.demuxInRateTier : null,
//             demuxToDecodeLagMs: (n.demuxToDecodeLagTier === 0 || n.demuxToDecodeLagTier === 1 || n.demuxToDecodeLagTier === 2) ? n.demuxToDecodeLagTier : null,
//             renderInOutGap: (n.renderInOutGapTier === 0 || n.renderInOutGapTier === 1 || n.renderInOutGapTier === 2) ? n.renderInOutGapTier : null,
//             downloadKbps: (n.downloadKbpsTier === 0 || n.downloadKbpsTier === 1 || n.downloadKbpsTier === 2) ? n.downloadKbpsTier : null,
//           },
//         },
//         metrics: {
//           jitterMs: Number.isFinite(Number(n.jitterMs)) ? Number(n.jitterMs) : null,
//           packetDelayMs: Number.isFinite(Number(n.packetDelayMs)) ? Number(n.packetDelayMs) : null,
//           dropFrameMs: Number.isFinite(Number(n.dropFrameMs)) ? Number(n.dropFrameMs) : null,
//           pFrameDelayMs: Number.isFinite(Number(n.pFrameDelayMs)) ? Number(n.pFrameDelayMs) : null,
//           iFrameDelayMs: Number.isFinite(Number(n.iFrameDelayMs)) ? Number(n.iFrameDelayMs) : null,
//           jitterTier: (n.jitterTier === 0 || n.jitterTier === 1 || n.jitterTier === 2) ? n.jitterTier : null,
//           packetDelayTier: (n.packetDelayTier === 0 || n.packetDelayTier === 1 || n.packetDelayTier === 2) ? n.packetDelayTier : null,
//           dropFrameTier: (n.dropFrameTier === 0 || n.dropFrameTier === 1 || n.dropFrameTier === 2) ? n.dropFrameTier : null,
//           pFrameDelayTier: (n.pFrameTier === 0 || n.pFrameTier === 1 || n.pFrameTier === 2) ? n.pFrameTier : null,
//           iFrameDelayTier: (n.iFrameTier === 0 || n.iFrameTier === 1 || n.iFrameTier === 2) ? n.iFrameTier : null,
//         },
//         avgSeverity: (n.sev === 0 || n.sev === 1 || n.sev === 2) ? n.sev : null,
//         worstMetricSeverity: (n.sev === 0 || n.sev === 1 || n.sev === 2) ? n.sev : null,
//         worstMetric: null,
//       })) as any
//     }
//     if (dimension === 'stream') {
//       return streams.map(s => {
//         const layers = layerCandidates(s)
//         const { avgSeverity, worstMetricSeverity, worstMetric } = computeAvgAndWorst(layers)
//         return { kind: 'stream' as const, s, avgSeverity, worstMetricSeverity, worstMetric }
//       })
//     }

//     const keyOf = (s: (typeof streams)[number]) => {
//       if (dimension === 'continent') return s.con
//       if (dimension === 'country') return (s.country ?? 'GB')
//       if (dimension === 'region') return (s as any).geoRegion ?? s.pop
//       if (dimension === 'studio') return (s as any).studio ?? 'unknown'
//       if (dimension === 'device_type') return (s as any).deviceType ?? 'unknown'
//       if (dimension === 'browser') return (s as any).browser ?? 'unknown'
//       if (dimension === 'cdn_edge') return (s as any).cdnEdge ?? 'unknown'
//       // user dimension: group by IP (until per-session dataset is wired through)
//       return (s as any)?.ip ?? 'unknown'
//     }
//     const labelOf = (k: string) => (
//       dimension === 'continent' ? getRegionLabel(k)
//         : dimension === 'country' ? getRegionLabel(k)
//           : k
//     )

//     const map = new Map<string, typeof streams>()
//     streams.forEach((s) => {
//       const k = keyOf(s)
//       const cur = map.get(k) ?? []
//       cur.push(s)
//       map.set(k, cur)
//     })

//     return [...map.entries()].map(([k, list]) => {
//       const usersTotal = list.reduce((a, x) => a + (typeof x.usersTotal === 'number' ? x.usersTotal : 0), 0)

//       // Aggregate throughput values with p95; tiers worst-case.
//       const jitterMs = p95(list.map(x => Number(x.metrics?.jitterMs)).filter(Number.isFinite))
//       const packetDelayMs = p95(list.map(x => Number(x.metrics?.packetDelayMs)).filter(Number.isFinite))
//       const dropFrameMs = p95(list.map(x => Number(x.metrics?.dropFrameMs)).filter(Number.isFinite))
//       const pFrameDelayMs = p95(list.map(x => Number(x.metrics?.pFrameDelayMs)).filter(Number.isFinite))
//       const iFrameDelayMs = p95(list.map(x => Number(x.metrics?.iFrameDelayMs)).filter(Number.isFinite))

//       const tierMin = (vals: Array<unknown>) => {
//         const ts = vals.map(v => (v === 0 || v === 1 || v === 2) ? (v as 0 | 1 | 2) : null).filter((v): v is 0 | 1 | 2 => v != null)
//         return (ts.length ? Math.min(...ts) : 2) as 0 | 1 | 2
//       }

//       const jitterTier = tierMin(list.map(x => x.metrics?.jitterTier))
//       const packetDelayTier = tierMin(list.map(x => x.metrics?.packetDelayTier))
//       const dropFrameTier = tierMin(list.map(x => x.metrics?.dropFrameTier))
//       const pFrameDelayTier = tierMin(list.map(x => x.metrics?.pFrameDelayTier))
//       const iFrameDelayTier = tierMin(list.map(x => x.metrics?.iFrameDelayTier))

//       // Aggregate layered severities from per-stream computed severities.
//       const per = list.map((s) => computeAvgAndWorst(layerCandidates(s)))
//       const worstList = per
//         .map((x) => x.worstMetricSeverity)
//         .filter((t): t is 0 | 1 | 2 => t === 0 || t === 1 || t === 2)
//       const avgList = per
//         .map((x) => x.avgSeverity)
//         .filter((t): t is 0 | 1 | 2 => t === 0 || t === 1 || t === 2)

//       const worstMetricSeverity = (worstList.length ? Math.min(...worstList) : null) as 0 | 1 | 2 | null
//       const avgSeverity = (avgList.length
//         ? (Math.max(0, Math.min(2, Math.round(avgList.reduce((a, b) => a + b, 0) / avgList.length))) as 0 | 1 | 2)
//         : null) as 0 | 1 | 2 | null

//       const worstMetric = (() => {
//         const layers = layerCandidates(list[0]!)
//         // compute group-tier/value for each layer using max tier + p95 value
//         const tierOf: Record<string, 0 | 1 | 2> = {
//           demuxInRate: tierMin(list.map(x => (x.pipeline as any)?.demuxInRateTier)),
//           demuxToDecodeLagMs: tierMin(list.map(x => (x.pipeline as any)?.demuxToDecodeLagTier)),
//           renderInOutGap: tierMin(list.map(x => (x.pipeline as any)?.renderInOutGapTier)),
//           downloadKbps: tierMin(list.map(x => (x.pipeline as any)?.downloadKbpsTier)),
//         }
//         const valOf: Record<string, number | null> = {
//           demuxInRate: p95(list.map(x => Number((x.pipeline as any)?.demuxInRate)).filter(Number.isFinite)),
//           demuxToDecodeLagMs: p95(list.map(x => Number((x.pipeline as any)?.demuxToDecodeLagMs)).filter(Number.isFinite)),
//           renderInOutGap: p95(list.map(x => Number((x.pipeline as any)?.renderInOutGap)).filter(Number.isFinite)),
//           downloadKbps: p95(list.map(x => Number((x.pipeline as any)?.downloadKbps)).filter(Number.isFinite)),
//         }
//         const updated = layers.map((l) => ({
//           ...l,
//           tier: tierOf[l.key],
//           valueText: valOf[l.key] == null ? null : (l.key === 'demuxToDecodeLagMs') ? fmtMs(valOf[l.key]!)
//             : l.key === 'demuxInRate' ? `${valOf[l.key]!.toFixed(1)}/s`
//               : l.key === 'downloadKbps' ? `${Math.round(valOf[l.key]!)}kbps`
//                 : `${Math.round(valOf[l.key]!)}`,
//         }))
//         return computeAvgAndWorst(updated).worstMetric
//       })()

//       return {
//         kind: 'group' as const,
//         id: k,
//         label: labelOf(k),
//         totalStreams: list.length,
//         usersTotal,
//         hex: {
//           values: {
//             demuxInRate: valOfGroup(list, 'demuxInRate'),
//             demuxToDecodeLagMs: valOfGroup(list, 'demuxToDecodeLagMs'),
//             renderInOutGap: valOfGroup(list, 'renderInOutGap'),
//             downloadKbps: valOfGroup(list, 'downloadKbps'),
//           },
//           tiers: {
//             demuxInRate: tierMin(list.map(x => (x.pipeline as any)?.demuxInRateTier)),
//             demuxToDecodeLagMs: tierMin(list.map(x => (x.pipeline as any)?.demuxToDecodeLagTier)),
//             renderInOutGap: tierMin(list.map(x => (x.pipeline as any)?.renderInOutGapTier)),
//             downloadKbps: tierMin(list.map(x => (x.pipeline as any)?.downloadKbpsTier)),
//           },
//         },
//         metrics: { jitterMs, packetDelayMs, dropFrameMs, pFrameDelayMs, iFrameDelayMs, jitterTier, packetDelayTier, dropFrameTier, pFrameDelayTier, iFrameDelayTier },
//         avgSeverity,
//         worstMetricSeverity,
//         worstMetric,
//       }
//     })
//   }, [dimension, streams])

//   const sorted = [...rows].sort((a, b) => {
//     const aw = a.worstMetricSeverity ?? 99
//     const bw = b.worstMetricSeverity ?? 99
//     if (bw !== aw) return aw - bw
//     const aa = a.avgSeverity ?? 99
//     const ba = b.avgSeverity ?? 99
//     return aa - ba
//   })  


//   const getDemuxInRateText = (tier: Tier): string => {
//     switch (tier) {
//       case 0:  return 'Stable'
//       case 1:  return 'Fluctuating'
//       case 2:  return 'Stalled'
//       case null: return '—'
//     }
//   }


//   return (
//     <div className="panel" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
//       <div className="panel-head">
//         <span className="panel-title">
//           {dimension === 'stream'
//             ? (drillStream ? `Stream: ${drillStream}` : 'Streams')
//             : (dimension === 'continent' ? 'Continents'
//               : dimension === 'country' ? 'Countries'
//                 : dimension === 'region' ? 'Regions'
//                   : dimension === 'studio' ? 'Studios'
//                     : dimension === 'device_type' ? 'Device types'
//                       : dimension === 'browser' ? 'Browsers'
//                         : dimension === 'cdn_edge' ? 'CDN edges'
//                           : 'Users')}
//         </span>
//         <span style={{ fontSize: 10, color: 'var(--t-2)' }}>{sorted.length}</span>
//         {(dataDebouncing || streamsLoading) && (
//           <span style={{ fontSize: 10, color: 'var(--t-2)', marginLeft: 8 }}>Updating…</span>
//         )}
//         {lastDataError && (
//           <span style={{ fontSize: 10, color: 'var(--crit)', marginLeft: 8 }}>API error</span>
//         )}
//         <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--t-2)' }}>
//           Scroll to load more
//         </span>
//       </div>

//       <div
//         style={{ overflow: 'auto', flex: 1, minHeight: 0 }}
//         onScroll={(e) => {
//           const el = e.currentTarget
//           const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120
//           if (nearBottom) setVisible(v => Math.min(sorted.length, v + 80))
//         }}
//       >
//         <table className="vms-table">
//           <thead>
//             <tr>
//               <th style={{ width: 120 }}>Severity</th>
//               <th style={{ width: 230 }}>Info</th>
//               {dimension === 'stream' && <th style={{ width: 220 }}>Source</th>}
//               {dimension === 'user' && <th style={{ width: 150 }}>IP</th>}
//               <th style={{ width: 96 }}>Streams</th>
//               <th style={{ width: 104 }}>Demux in</th>
//               <th style={{ width: 124 }}>Demux→Dec</th>
//               <th style={{ width: 110 }}>Render in/out</th>
//               <th style={{ width: 110 }}>Download</th>
//               <th style={{ width: 86 }}>Jitter</th>
//               <th style={{ width: 104 }}>Packet delay</th>
//               <th style={{ width: 110 }}>Drop frame</th>
//               <th style={{ width: 110 }}>P-frame</th>
//               <th style={{ width: 110 }}>I-frame</th>
//             </tr>
//           </thead>
//           <tbody>
//             {(() => {
//               const extra = (dimension === 'stream' ? 1 : 0) + (dimension === 'user' ? 1 : 0)
//               const colSpan = 12 + extra
//               return (
//                 <>
//             {sorted.length === 0 && !streamsLoading && !dataDebouncing && (
//               <tr>
//                 <td colSpan={colSpan} style={{ padding: 18, textAlign: 'center', color: 'var(--t-2)' }}>
//                   No data
//                 </td>
//               </tr>
//             )}
//             {sorted.length === 0 && (streamsLoading || dataDebouncing) && (
//               <tr>
//                 <td colSpan={colSpan} style={{ padding: 18, textAlign: 'center', color: 'var(--t-2)' }}>
//                   Loading…
//                 </td>
//               </tr>
//             )}
//                 </>
//               )
//             })()}
//             {sorted.slice(0, visible).map((r) => {
//               const isStream = r.kind === 'stream'
//               const id = isStream ? r.s.sid : r.id
//               const onClick = () => {
//                 if (dimension === 'stream' && isStream) {
//                   addComparisonStream(r.s.sid)
//                   drillToStream(r.s.sid)
//                 }
//                 if (dimension === 'region' && !isStream) drillToRegion(r.id)
//                 if (dimension === 'user' && !isStream) {
//                   setUserId(r.id)
//                   setView('deep')
//                 }
//               }
//               const info = isStream
//                 ? {
//                     country: getRegionLabel(r.s.country ?? 'GB'),
//                     users: typeof r.s.usersTotal === 'number' ? r.s.usersTotal : null,
//                     url: r.s.sourceUrl ?? null,
//                     id: r.s.sid,
//                   }
//                 : {
//                     country: '—',
//                     users: r.usersTotal,
//                     url: null,
//                     id: r.id,
//                   }

//               const m = isStream ? r.s.metrics : r.metrics
//               const tier = (k: keyof NonNullable<typeof m>) => (m as any)?.[k] as any
//               const p = isStream ? (r.s.pipeline as any) : null

//               const worstTip = r.worstMetric
//                 ? `${r.worstMetric.label}: ${r.worstMetric.valueText ?? '—'} (tier ${r.worstMetric.tier})`
//                 : `Worst metric: —`
//               const tip = `${worstTip}\nAverageSeverity: ${r.avgSeverity}\nWorstMetricSeverity: ${r.worstMetricSeverity}`

//               const tierText = (t: any) => (t === 0 || t === 1 || t === 2) ? String(t) : '—'
//               const valStyle = (t: any) => ({ color: sevColor((t === 0 || t === 1 || t === 2) ? t : null) })
//               const cellStyle = (t: any) => ({ color: sevColor((t === 0 || t === 1 || t === 2) ? t : null), fontWeight: 700 as const })

//               const totalStreams = isStream ? 1 : r.totalStreams

//               const hexCell = (key: LayerKey) => {
//                 if (isStream) {
//                   const val = (r.s.pipeline as any)?.[key]
//                   const tKey = (key + 'Tier') as any
//                   const tr = (r.s.pipeline as any)?.[tKey]
//                   const txt = val === null || val === undefined || !Number.isFinite(Number(val))
//                     ? '—'
//                     : (key === 'demuxToDecodeLagMs') ? `${Math.round(Number(val))}ms`
//                       : key === 'demuxInRate' ? getDemuxInRateText(tr as Tier)
//                         : key === 'downloadKbps' ? `${Math.round(Number(val))}kbps`
//                           : key === 'renderInOutGap' ? `${val}`
//                           : `${Math.round(Number(val))}`
//                   return <div style={cellStyle(tr)}>{txt}</div>
//                 }
//                 const v = (r as any)?.hex?.values?.[key] as number | null | undefined
//                 const tr = (r as any)?.hex?.tiers?.[key] as 0 | 1 | 2 | null | undefined
//                 const txt = v == null || !Number.isFinite(Number(v))
//                   ? '—'
//                   : (key === 'demuxToDecodeLagMs') ? `${Math.round(Number(v))}ms`
//                     : key === 'demuxInRate' ? getDemuxInRateText(tr as Tier)
//                       : key === 'downloadKbps' ? `${Math.round(Number(v))}kbps`
//                         : key === 'renderInOutGap' ? `${v}`
//                         : `${Math.round(Number(v))}`
//                 return <div style={cellStyle(tr)}>{txt}</div>
//               }

//               return (
//                 <tr
//                   key={id}
//                   onClick={onClick}
//                   style={{ cursor: (dimension === 'region' && !isStream) || (dimension === 'stream' && isStream) ? 'pointer' : 'default', background: isStream && drillStream === r.s.sid ? 'var(--bg-2)' : undefined }}
//                 >
//                   <td>
//                     {severityCell(r.avgSeverity, r.worstMetricSeverity, tip)}
//                   </td>
//                   <td>
//                     <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                       <div style={{ fontWeight: 600, color: 'var(--t-0)' }}>
//                         {isStream ? info.id : r.label}
//                       </div>
//                       <div style={{ fontSize: 10, color: 'var(--t-2)' }}>
//                         {(info.users == null ? '—' : info.users)} Users
//                       </div>
//                     </div>
//                   </td>
//                   {dimension === 'stream' && (
//                     <td style={{ color: 'var(--t-2)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
//                       {isStream ? (info.url ?? '—') : '—'}
//                     </td>
//                   )}
//                   {dimension === 'user' && (
//                     <td style={{ color: 'var(--t-2)', fontFamily: 'var(--font-mono)' }}>
//                       {isStream ? '—' : r.id}
//                     </td>
//                   )}
//                   <td style={{ color: 'var(--t-2)' }}>{totalStreams}</td>
//                   <td>{hexCell('demuxInRate')}</td>
//                   <td>{hexCell('demuxToDecodeLagMs')}</td>
//                   <td>{hexCell('renderInOutGap')}</td>
//                   <td>{hexCell('downloadKbps')}</td>
//                   <td style={{ color: 'var(--t-2)' }}>{fmt((m as any)?.jitterMs, 'ms')}</td>
//                   <td style={{ color: 'var(--t-2)' }}>{fmt((m as any)?.packetDelayMs, 'ms')}</td>
//                   <td>
//                     <div style={{ ...valStyle(tier('dropFrameTier')), fontWeight: 700 }}>{fmt((m as any)?.dropFrameMs, 'ms')}</div>
//                   </td>
//                   <td>
//                     <div style={{ ...valStyle(tier('pFrameDelayTier')), fontWeight: 700 }}>{fmt((m as any)?.pFrameDelayMs, 'ms')}</div>
//                   </td>
//                   <td>
//                     <div style={{ ...valStyle(tier('iFrameDelayTier')), fontWeight: 700 }}>{fmt((m as any)?.iFrameDelayMs, 'ms')}</div>
//                   </td>
//                 </tr>
//               )
//             })}
//             {visible < sorted.length && (
//               <tr>
//                 <td colSpan={5} style={{ padding: '8px 8px', color: 'var(--t-2)', fontSize: 10 }}>
//                   Loading more…
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   )
// }
import { useMemo } from 'react'
import { useStore, useFilteredStreams } from '@/store'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { Dimension } from '@/types'
import { compareRowsBySeverity } from './rows'
import StreamRowView from './StreamRowView'
import TableHeader from './TableHeader'
import { Row } from './types'

function columnCount(dimension: Dimension): number {
  // Severity, Info, Ticks, Users, From, To, 4 hex = 10, plus conditional
  if (dimension === 'stream') return 11 + 1 // +Source
  if (dimension === 'user') return 11 + 1 + 1 // +Streams +IP
  return 11 + 1 // +Streams
}

export type StreamTableProps = {
  /** Optional explicit rows (e.g. Deep Dive historical windows). */
  rows?: Row[]
  /** Override empty-state copy. */
  emptyText?: string
  /** Optional header title override. */
  titleOverride?: string
}

export default function StreamTable({
  rows: rowsProp,
  emptyText,
  titleOverride,
}: StreamTableProps = {}) {
  const streams           = useFilteredStreams()
  const dimension         = useStore((s) => s.dimension) as Dimension
  const drillStream       = useStore((s) => s.drillStream)
  const drillToStream     = useStore((s) => s.drillToStream)
  const drillToNode       = useStore((s) => s.drillToNode)
  const addComparisonStream = useStore((s) => s.addComparisonStream)
  const setUserId         = useStore((s) => s.setUserId)
  const setFilterOnly     = useStore((s) => s.setFilterOnly)
  const setDimension      = useStore((s) => s.setDimension)
  const setView           = useStore((s) => s.setView)
  const dataDebouncing    = useStore((s) => s.dataDebouncing)
  const streamsLoading    = useStore((s) => s.streamsLoading)
  const wsUiPaused        = useStore((s) => s.wsUiPaused)
  const lastDataError     = useStore((s) => s.lastDataError)

  const rows = useMemo(
    () => (rowsProp != null ? rowsProp : (streams as Row[])),
    [rowsProp, streams],
  )

  const sorted = useMemo(
    () => rows.slice().sort(compareRowsBySeverity),
    [rows],
  )

  const { visible, onScroll, hasMore } = useInfiniteScroll(sorted.length)

  const handleActivate = (row: Row) => {
    if (dimension === 'stream' && row.kind === 'stream') {
      const sid = String(row.streamId || row.value || '').trim()
      if (!sid) return
      addComparisonStream(sid)
      drillToStream(sid)
      return
    }
    if ((dimension === 'continent' || dimension === 'country' || dimension === 'region') && row.kind === 'group') {
      drillToNode({ dimension, value: row.value })
      return
    }
    if ((dimension === 'studio' || dimension === 'game') && row.kind === 'group') {
      // Scope filters to this studio/game and switch Hex/table to stream stress points.
      setFilterOnly(dimension, row.value)
      setDimension('stream')
      setView('hex')
      return
    }
    if (dimension === 'user' && row.kind === 'group') {
      setUserId(row.value)
      setView('deep')
    }
  }

  const isLoading = streamsLoading || dataDebouncing || wsUiPaused
  const colSpan = columnCount(dimension)

  return (
    <div className="panel" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <TableHeader
        dimension={dimension}
        drillStream={drillStream}
        rowCount={sorted.length}
        loading={isLoading}
        errored={Boolean(lastDataError)}
        titleOverride={titleOverride}
      />

      <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }} onScroll={onScroll}>
        <table className="vms-table">
          <thead>
            <tr>
              <th style={{ width: 120 }}>Severity</th>
              <th style={{ width: 230 }}>Info</th>
              {dimension === 'stream' && <th style={{ width: 220 }}>Source</th>}
              {dimension === 'user' && <th style={{ width: 150 }}>IP</th>}
              <th style={{ width: 64 }}>Ticks</th>
              <th style={{ width: 72 }}>Users</th>
              {dimension !== 'stream' && <th style={{ width: 80 }}>Streams</th>}
              <th style={{ width: 90 }}>From time</th>
              <th style={{ width: 90 }}>To time</th>
              <th style={{ width: 104 }}>Demux rate</th>
              <th style={{ width: 124 }}>Demux→Dec</th>
              <th style={{ width: 110 }}>Render in/out</th>
              <th style={{ width: 110 }}>Download</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={colSpan} style={{ padding: 18, textAlign: 'center', color: 'var(--t-2)' }}>
                  {isLoading ? 'Loading…' : (emptyText ?? 'No data')}
                </td>
              </tr>
            )}
            {sorted.slice(0, visible).map((row) => (
              <StreamRowView
                key={row.kind === 'stream'
                  ? `${String(row.streamId || row.value)}:${row.windowStartMs}:${row.windowEndMs}`
                  : row.value}
                row={row}
                dimension={dimension}
                drillStream={drillStream}
                onActivate={handleActivate}
              />
            ))}
            {hasMore && (
              <tr>
                <td colSpan={colSpan} style={{ padding: '8px 8px', color: 'var(--t-2)', fontSize: 10 }}>
                  Loading more…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}